import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Placeholder } from 'modules/Finance/Wallets/subcomponents/Chart/subcomponents/Placeholder'
import { DoughnutChart } from 'modules/Finance/Wallets/subcomponents/Chart/subcomponents/DoughnutChart'
import { Legend } from 'modules/Finance/Wallets/subcomponents/Chart/subcomponents/Legend'
import { Button, Grid, Icon, Typography } from 'gu-ui-kit'
import { TRANSACTION_CONFIG } from 'modules/Finance/Wallets/config/config'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import formatMoney from 'utils/formatMoney'
import { BarChart } from 'modules/Finance/Wallets/subcomponents/Chart/subcomponents/BarChart'
import { useSelector } from 'react-redux'
import styles from 'modules/Finance/Wallets/subcomponents/Chart/Chart.module.scss'

const CATEGORY_TYPES = ['out', 'in']

export const Chart = ({ className = '' }) => {
  const { t } = useTranslation()
  const [chartShown, setChartShown] = useState(false)

  const [categoryActiveType, setCategoryType] = useState('out')
  const breakpoint = useBreakpoint()
  const {
    activeWalletID,
    wallets: { data: wallets = [] } = {},
    transactions: { loading, loaded, categories = {} } = {},
  } = useSelector((state) => state['finance/wallets'])

  const { currency } = useMemo(
    () => wallets?.find(({ id }) => id === activeWalletID) ?? {},
    [wallets, activeWalletID],
  )

  const stateCategories = useMemo(
    () =>
      Object.entries(categories)
        .filter(([, value = {}]) => 'in' in value || 'out' in value)
        .filter(([, value = {}]) => [categoryActiveType] in value)
        .map(([key, value]) => [key, value[categoryActiveType]])
        .filter(([, value]) => value > 0),
    [categories, categoryActiveType],
  )

  const handleClickTabs = (type) => {
    setCategoryType(type)
  }

  const transactionColors = useMemo(
    () =>
      stateCategories.map(
        ([category]) => TRANSACTION_CONFIG?.[category]?.[categoryActiveType]?.color ?? '#000',
      ),
    [categoryActiveType, stateCategories],
  )

  if (loading || !loaded) {
    return <Placeholder className={className} />
  }

  return (
    <Grid
      direction={'column'}
      gap={8}
      className={clsx(styles['chart'], className)}
    >
      <Grid
        alignItems={'center'}
        justifyContent={'between'}
        wrap={false}
        className={clsx({
          ['margin-bottom-8']: !breakpoint.md,
        })}
      >
        <Grid
          alignItems={'center'}
          justifyContent={breakpoint.md ? 'stretch' : 'start'}
          className={styles['chart__tabs']}
          wrap={false}
          gap={8}
        >
          {CATEGORY_TYPES.map((type) => (
            <Typography
              key={type}
              variant={'Body-15'}
              component={'button'}
              color={categoryActiveType === type ? 'Label-Onsurface' : 'Label-Fifth'}
              className={clsx(styles['chart__tabs-tab'], {
                [styles['chart__tabs-tab_active']]: categoryActiveType === type,
              })}
              onClick={() => handleClickTabs(type)}
            >
              {t('wallets::chart.' + type)}
            </Typography>
          ))}
        </Grid>
        {!breakpoint.md && (
          <Button
            variant={'ghost'}
            size={'m'}
            link
            endIcon={<Icon name={chartShown ? 'arrow-up' : 'arrow-down'} />}
            onClick={() => setChartShown((prevState) => !prevState)}
            noWrap
          >
            <span
              dangerouslySetInnerHTML={{
                __html: formatMoney(
                  parseFloat(stateCategories.reduce((acc, [, value]) => acc + value, 0)).toFixed(2),
                  currency,
                ),
              }}
            />
          </Button>
        )}
      </Grid>

      {!breakpoint.md && !chartShown && (
        <BarChart
          currency={currency}
          labels={stateCategories.map(([category]) => t(`wallets::chart.categories.${category}`))}
          colors={transactionColors}
          data={stateCategories.map(([, value]) => value)}
        />
      )}

      {(breakpoint.md || chartShown) && (
        <div className={styles['chart__doughnut-wrapper']}>
          <DoughnutChart
            currency={currency}
            colors={transactionColors}
            data={stateCategories.map(([, value]) => value)}
          />
        </div>
      )}

      {(breakpoint.md || chartShown) && (
        <div className={styles['chart__context']}>
          <Legend
            data={{
              categories: stateCategories,
              colors: transactionColors,
              currency,
            }}
            categoryActiveType={categoryActiveType}
          />
        </div>
      )}
    </Grid>
  )
}

Chart.propTypes = {
  className: PropTypes.string,
}
